import React, { useEffect, useState } from 'react'
import { publishToOpenWebUI, publishToClaude, updateSkill, trackEvent } from '../api'

interface Step {
  label: string
  status: 'pending' | 'running' | 'done' | 'error'
  error?: string
}

interface PublishModalProps {
  skillId: string
  destinations: { openwebui: boolean; claude: boolean }
  onClose: () => void
  onPublished?: () => void
}

export default function PublishModal({ skillId, destinations, onClose, onPublished }: PublishModalProps) {
  const [steps, setSteps] = useState<Step[]>([])
  const [done, setDone] = useState(false)

  useEffect(() => {
    const statusStepIdx = 1 + (destinations.openwebui ? 1 : 0) + (destinations.claude ? 1 : 0)
    const initialSteps: Step[] = [
      { label: 'Preparing skill…', status: 'pending' },
      ...(destinations.openwebui ? [{ label: 'Publishing to Open WebUI…', status: 'pending' as const }] : []),
      ...(destinations.claude ? [{ label: 'Publishing to Claude API…', status: 'pending' as const }] : []),
      { label: 'Updating status to production…', status: 'pending' },
      { label: 'Done', status: 'pending' },
    ]
    setSteps(initialSteps)

    const run = async () => {
      const set = (i: number, update: Partial<Step>) =>
        setSteps(s => s.map((step, idx) => idx === i ? { ...step, ...update } : step))

      // Step 0: Preparing
      set(0, { status: 'running' })
      await delay(300)
      set(0, { status: 'done' })

      let stepIdx = 1
      let anySuccess = false

      // Open WebUI
      if (destinations.openwebui) {
        set(stepIdx, { status: 'running' })
        try {
          await publishToOpenWebUI(skillId)
          set(stepIdx, { status: 'done' })
          anySuccess = true
        } catch (e: any) {
          set(stepIdx, { status: 'error', error: e?.response?.data?.detail || 'Failed' })
        }
        stepIdx++
      }

      // Claude API
      if (destinations.claude) {
        set(stepIdx, { status: 'running' })
        try {
          await publishToClaude(skillId)
          set(stepIdx, { status: 'done' })
          anySuccess = true
        } catch (e: any) {
          set(stepIdx, { status: 'error', error: e?.response?.data?.detail || 'Failed' })
        }
        stepIdx++
      }

      // Update status to production in Supabase + rewrite GitHub frontmatter
      set(statusStepIdx, { status: 'running' })
      try {
        await updateSkill(skillId, { status: 'production' })
        set(statusStepIdx, { status: 'done' })
        onPublished?.()
        trackEvent('skill_published', skillId, { destinations })
      } catch (e: any) {
        set(statusStepIdx, { status: 'error', error: 'Status update failed' })
      }

      // Done
      set(statusStepIdx + 1, { status: 'done' })
      setDone(true)
    }

    run()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 bg-black/25 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-[#e5e5e3] dark:border-[#2e2e2e] p-6 w-[360px] shadow-[0_4px_24px_rgba(0,0,0,0.12)]">
        <div className="text-[15px] font-semibold dark:text-[#f0f0ef] mb-5">Publishing skill</div>

        <div className="flex flex-col gap-3 mb-5">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                {step.status === 'running' && <div className="spinner" />}
                {step.status === 'done' && (
                  <div className="w-5 h-5 rounded-full bg-[#16a34a] flex items-center justify-center">
                    <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                      <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
                {step.status === 'error' && (
                  <div className="w-5 h-5 rounded-full bg-[#e53e3e] flex items-center justify-center">
                    <span className="text-white text-[11px] font-bold">✕</span>
                  </div>
                )}
                {step.status === 'pending' && (
                  <div className="w-4 h-4 rounded-full border border-[#e5e5e3] dark:border-[#2e2e2e]" />
                )}
              </div>
              <div>
                <span className={`text-[13px] ${
                  step.status === 'done' ? 'text-[#1a1a1a] dark:text-[#f0f0ef]' :
                  step.status === 'running' ? 'text-[#1a1a1a] dark:text-[#f0f0ef] font-medium' :
                  step.status === 'error' ? 'text-[#e53e3e]' :
                  'text-[#bbb] dark:text-[#444]'
                }`}>
                  {step.label}
                </span>
                {step.error && <div className="text-[11px] text-[#e53e3e] mt-0.5">{step.error}</div>}
              </div>
            </div>
          ))}
        </div>

        {done && (
          <button
            onClick={onClose}
            className="w-full py-2 bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a] rounded-[7px] text-[13px] font-medium hover:bg-[#333] dark:hover:bg-[#ddd] cursor-pointer"
          >
            Done
          </button>
        )}
      </div>
    </div>
  )
}

function delay(ms: number) { return new Promise(r => setTimeout(r, ms)) }
